import {FormFieldItem, getStatusText, renderFormField} from '@common'
import {ContractStatus, FormFieldType, TranslationKey} from '@enums'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {Accordion, AccordionSummary, AccordionDetails, Grid, Stack, Typography} from '@mui/material'

import {useEffect, useMemo, useState} from 'react'

import {t} from 'i18next'

import {ClearButton} from './style'

interface Props {
  formFields: FormFieldItem[]
  onApplyFilter: (newObject: {[k: string]: string | string[]}) => void
  onClearFilter: () => void
}
export function TableFilters(props: Props) {
  const [isOpen, setIsOpen] = useState(false)
  const {formFields, onApplyFilter, onClearFilter} = props

  const onClear = () => {
    setTempFilters(
      Object.fromEntries(namedFormFields.map(item => [item.name, item.fieldType === FormFieldType.INPUT ? '' : []])),
    )
    onClearFilter()
  }

  type NamedField = FormFieldItem & {
    name: string
    value?: string | string[]
  }

  const namedFormFields = useMemo(() => formFields.filter((item): item is NamedField => 'name' in item), [formFields])

  const [tempFilters, setTempFilters] = useState<Record<string, string | string[]>>(() =>
    Object.fromEntries(namedFormFields.map(item => [item.name, item.value as string | string[]])),
  )

  useEffect(() => {
    setTempFilters(
      Object.fromEntries(
        namedFormFields.map(item => [item.name, item.value ?? (item.fieldType === FormFieldType.INPUT ? '' : [])]),
      ),
    )
  }, [namedFormFields])

  const isApplyDisabled = useMemo(() => {
    return namedFormFields.every(item => tempFilters[item.name] === item.value)
  }, [namedFormFields, tempFilters])

  const isClearDisabled = useMemo(() => {
    return Object.values(tempFilters).every(item => (typeof item === 'string' ? item === '' : item.length === 0))
  }, [tempFilters])

  const reappliedFormFields = useMemo(
    () =>
      namedFormFields.map(item => ({
        ...item,
        value: tempFilters[item.name],
        onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
          setTempFilters(prev => ({
            ...prev,
            [item.name]: event.target.value,
          })),
      })),
    [namedFormFields, tempFilters, formFields],
  )

  const filterLabels = useMemo(() => {
    return namedFormFields
      .filter(item => (typeof item.value === 'string' ? item.value !== '' : (item.value as string[]).length > 0))
      .map(({label, value}) => ({label, value}))
  }, [namedFormFields])

  const renderFilterValue = (item: {value: string | string[] | undefined; label: string | undefined}) => {
    return item.label === t(TranslationKey.Status)
      ? (item.value as string[])?.map(value => getStatusText(t, value as ContractStatus)).join(', ')
      : typeof item.value === 'string'
        ? item.value
        : item.value?.join(', ')
  }

  return (
    <Accordion
      expanded={isOpen}
      onChange={() => setIsOpen(prev => !prev)}
      style={{flex: 1, borderRadius: '1rem'}}
      sx={{
        '&::before': {
          display: 'none',
        },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack flexDirection={'row'} gap={1}>
          <Typography fontWeight={'bold'}>{t(TranslationKey.Filters)}</Typography>
          {filterLabels.map(item => (
            <Stack flexDirection={'row'} gap={0.5}>
              <Typography>{`| ${item.label}: `}</Typography>
              <Typography fontWeight={'bold'}>{renderFilterValue(item)}</Typography>
            </Stack>
          ))}
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        {
          <Grid container columnSpacing={2} rowSpacing={1}>
            {reappliedFormFields.map((item, index) => (
              <Grid item key={`${item.label}-${index}`} sm={3}>
                {renderFormField(item as FormFieldItem)}
              </Grid>
            ))}
            <Grid item sm={3}>
              <ClearButton variant="contained" onClick={() => onApplyFilter(tempFilters)} disabled={isApplyDisabled}>
                {t(TranslationKey.Apply)}
              </ClearButton>
            </Grid>
            <Grid item sm={3}>
              <ClearButton variant="outlined" onClick={onClear} disabled={isClearDisabled}>
                {t(TranslationKey.Clear)}
              </ClearButton>
            </Grid>
          </Grid>
        }
      </AccordionDetails>
    </Accordion>
  )
}
